// "Revenue" card of a charter booking: operator price, margin, fee, total, pre-auth and remaining amount.
import { Card, Descriptions, Space, Tag, Typography } from 'antd';

import type { CharterBookingDetail } from '../../api/types';
import { amount, commissionLabel, gbp } from '../../data/format';
import { RefundOutsideAppTag, ReleaseFailedTag } from '../../components/CancellationMoneyTags';

const { Text } = Typography;

export function RevenueCard({
  rev,
  b,
}: {
  rev: CharterBookingDetail['revenue'];
  /** What a cancellation left on the booking's money. */
  b: Pick<CharterBookingDetail, 'refundOutsideAppAt' | 'holdReleaseFailedAt' | 'holdReleaseFailure' | 'paymentMethod'>;
}) {
  return (
    <Card title="Revenue">
      <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
        <Descriptions.Item label="Operator price (GBP)">{gbp(rev.operatorPriceGbp)}</Descriptions.Item>
        <Descriptions.Item label="Operator price (operator's currency)">
          {rev.operatorPriceAmount != null
            ? `${amount(rev.operatorPriceAmount)} ${rev.operatorPriceCcy ?? ''}`.trim()
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Margin">{gbp(rev.marginGbp)}</Descriptions.Item>
        <Descriptions.Item label={commissionLabel()}>{gbp(rev.processingFeeGbp)}</Descriptions.Item>
        {/* SHAR-1374 — the fixed fee(s) the customer paid (quote + pre-auth), so the lines add up to the total. */}
        <Descriptions.Item label="Fixed fee">{gbp(rev.fixedFeeGbp)}</Descriptions.Item>
        <Descriptions.Item label="Total price"><Text strong>{gbp(rev.totalGbp)}</Text></Descriptions.Item>
        <Descriptions.Item label="Pre-auth paid by user">
          {/* The AC asks for "Expired" rather than a figure once the hold has
              lapsed: no money was taken, and an amount would say it was. */}
          {rev.preAuthExpired ? <Text type="danger">Expired</Text> : gbp(rev.preAuthPaidGbp)}
        </Descriptions.Item>
        <Descriptions.Item label="Remaining amount">
          <Space wrap>
            {gbp(rev.remainingGbp)}
            {rev.remainingPaid ? <Tag color="green">Paid</Tag> : <Tag color="orange">Not paid</Tag>}
            {/* the transfer wording only when the balance actually arrived: a wire
                booking can be marked for its captured card deposit alone */}
            <RefundOutsideAppTag
              at={b.refundOutsideAppAt}
              paidBy={b.paymentMethod === 'wire_transfer' && rev.remainingPaid ? 'wire_transfer' : 'card'}
            />
            <ReleaseFailedTag at={b.holdReleaseFailedAt} reason={b.holdReleaseFailure} />
          </Space>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
}
